import { STATUSPAGE } from '@repo/ui';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, Col, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { useKolbDistribution } from '../../../../hooks/use-kolb-distribution';
import { KolbConfirmationModal } from './kolb-confirmation-modal';
import './styles.css';

interface IProps {
  entityId: string;
}

export const KolbDistributionTabContent = ({ entityId }: IProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupsFilterText, setGroupsFilterText] = useState('');

  const {
    courses,
    groups,
    selectedCourse,
    selectedGroupIds,
    distributionResult,
    loadingCourses,
    loadingGroups,
    loadingDistribution,
    requestGetCourses,
    requestGetGroupsByCourse,
    requestDistributeStudents,
    requestGetTotalStudentsWithKolb,
    setSelectedCourse,
    toggleGroupSelection,
    toggleAllGroups,
    clearSelection,
    getTotalStudentsWithKolb,
  } = useKolbDistribution();

  useEffect(() => {
    if (entityId) {
      requestGetCourses(entityId);
    }
  }, [entityId, requestGetCourses]);

  useEffect(() => {
    if (entityId && selectedCourse) {
      requestGetTotalStudentsWithKolb(
        entityId,
        selectedCourse.id,
        selectedGroupIds.length > 0 ? selectedGroupIds : undefined
      );
    }
  }, [entityId, selectedCourse, selectedGroupIds, requestGetTotalStudentsWithKolb]);

  const handleCourseChange = (courseId: string) => {
    const course = courses.find((c) => c.id === courseId);
    setSelectedCourse(course || null);
    if (course) {
      requestGetGroupsByCourse(course.id, entityId);
    }
  };

  const handleDistribute = async () => {
    if (!selectedCourse) return;

    try {
      const groupIds =
        selectedGroupIds.length > 0 ? selectedGroupIds : undefined;
      await requestDistributeStudents(
        entityId,
        selectedCourse.id,
        groupIds
      );
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Distribution failed:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao distribuir estudantes';
      toast.error(errorMessage);
    }
  };

  const isDistributingAll = selectedGroupIds.length === 0;
  const allGroupsSelected =
    selectedGroupIds.length === groups.length && groups.length > 0;

  return (
    <Card className="mt-5">
      <CardBody>
        <div className='kolb-distribution-container'>
      <Row className='mb-4'>
        <Col>
          <h4 className='fw-bold'>Distribuição de Estudantes por Kolb</h4>
          <p className='text-muted'>
            Distribua automaticamente estudantes em grupos baseado em seus
            estilos de aprendizagem Kolb
          </p>
        </Col>
      </Row>

      {/* Step 1: Select Course */}
      <Card className='card-section mb-4'>
        <div className='section-header'>
          <div className='step-badge'>1</div>
          <h5>Selecionar Curso</h5>
        </div>

        {loadingCourses === STATUSPAGE.LOADING ? (
          <div className='loading-state'>
            <Spinner size='sm' />
            <p>Carregando cursos...</p>
          </div>
        ) : courses.length === 0 ? (
          <Alert color='warning' className='mb-0'>
            Nenhum curso encontrado para esta organização.
          </Alert>
        ) : (
          <div className='course-selector'>
            <select
              className='form-select'
              value={selectedCourse?.id || ''}
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value=''>Selecione um curso...</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </Card>

      {/* Step 2: Select Groups */}
      {selectedCourse && (
        <Card className='card-section mb-4'>
          <div className='section-header'>
            <div className='step-badge'>2</div>
            <h5>Selecionar Grupos</h5>
          </div>

          {loadingGroups === STATUSPAGE.LOADING ? (
            <div className='loading-state'>
              <Spinner size='sm' />
              <p>Carregando grupos...</p>
            </div>
          ) : groups.length === 0 ? (
            <Alert color='warning' className='mb-0'>
              Nenhum grupo encontrado para este curso.
            </Alert>
          ) : (
            <>
              <div className='group-selector-toolbar'>
                <div className='checkbox-control'>
                  <input
                    type='checkbox'
                    id='select-all'
                    checked={allGroupsSelected}
                    onChange={(e) => toggleAllGroups(e.target.checked)}
                  />
                  <label htmlFor='select-all' className='mb-0'>
                    Selecionar todos os grupos ({groups.length})
                  </label>
                </div>
                <span className='selected-count'>
                  {selectedGroupIds.length} selecionado
                  {selectedGroupIds.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className='mb-3' style={{ padding: '0 20px', marginTop: '16px' }}>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Buscar grupos...'
                  value={groupsFilterText}
                  onChange={(e) => setGroupsFilterText(e.target.value)}
                />
              </div>

              {groupsFilterText && (
              <div className='groups-list'>
                {groups
                  .filter((group) =>
                    group.name
                      .toLowerCase()
                      .includes(groupsFilterText.toLowerCase())
                  )
                  .length === 0 ? (
                  <Alert color='info' className='mb-0'>
                    Nenhum grupo encontrado com "{groupsFilterText}"
                  </Alert>
                ) : (
                  groups
                    .filter((group) =>
                      group.name
                        .toLowerCase()
                        .includes(groupsFilterText.toLowerCase())
                    )
                    .map((group) => (
                  <div key={group.id} className='group-card'>
                    <div className='group-checkbox'>
                      <input
                        type='checkbox'
                        id={`group-${group.id}`}
                        checked={selectedGroupIds.includes(group.id)}
                        onChange={() => toggleGroupSelection(group.id)}
                      />
                      <label
                        htmlFor={`group-${group.id}`}
                        className='mb-0 flex-grow-1'
                      >
                        <div className='group-info'>
                          <span className='group-name'>{group.name}</span>
                          <span className='group-stats'>
                            {group.studentsWithKolb}/{group.totalStudents}{' '}
                            com Kolb
                          </span>
                        </div>
                      </label>
                    </div>

                    <div className='group-kolb-distribution'>
                      {Object.entries(group.kolbDistribution)
                        .filter(([, count]) => count > 0)
                        .map(([profile, count]) => (
                          <div key={profile} className='kolb-badge' title={`${count} estudante${count !== 1 ? 's' : ''} com perfil ${getKolbLabel(profile)}`}>
                            <Badge
                              pill
                              color={getKolbColor(profile)}
                              className='me-2'
                            >
                              {getKolbLabel(profile)}
                            </Badge>
                            <span className='kolb-count'>{count} estudante{count !== 1 ? 's' : ''}</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                    ))
                )}
              </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Step 3: Distribution Summary */}
      {selectedCourse && groups.length > 0 && (
        <Card className='card-section mb-4'>
          <div className='section-header'>
            <div className='step-badge'>3</div>
            <h5>Resumo da Distribuição</h5>
          </div>

          <div className='summary-info'>
            <div className='summary-row'>
              <span className='label'>Estratégia:</span>
              <span className='value'>
                {isDistributingAll ? (
                  <>
                    <Badge color='danger' className='me-2'>
                      Todos os grupos
                    </Badge>
                    Será usada a melhor estratégia disponível
                  </>
                ) : (
                  <>
                    <Badge color='info' className='me-2'>
                      Grupos Selecionados
                    </Badge>
                    {selectedGroupIds.length} grupo
                    {selectedGroupIds.length !== 1 ? 's' : ''}
                  </>
                )}
              </span>
            </div>

            <div className='summary-row'>
              <span className='label'>Estudantes com Kolb:</span>
              <span className='value highlight'>{getTotalStudentsWithKolb()}</span>
            </div>

            <div className='summary-row'>
              <span className='label'>Grupos Afetados:</span>
              <span className='value highlight'>
                {selectedGroupIds.length === 0
                  ? groups.length
                  : selectedGroupIds.length}
              </span>
            </div>
          </div>

          {isDistributingAll && (
            <Alert color='warning' className='alert-distribute-all'>
              <i className='ri-error-warning-line me-2'></i>
              <strong>Atenção:</strong> Distribuindo em TODOS os grupos. Uma
              confirmação será solicitada.
            </Alert>
          )}

          <div className='kolb-profiles-section'>
            <h6 className='kolb-profiles-title'>Perfis de Aprendizagem Kolb</h6>
            <div className='kolb-profiles-grid'>
              <div className='kolb-profile-card'>
                <div className='profile-badge-wrapper'>
                  <Badge pill color='primary'>AC</Badge>
                </div>
                <div className='profile-info'>
                  <strong>Convergente</strong>
                  <small>Prático e focado na aplicação de ideias. Prefere tarefas técnicas.</small>
                </div>
              </div>
              <div className='kolb-profile-card'>
                <div className='profile-badge-wrapper'>
                  <Badge pill color='success'>AE</Badge>
                </div>
                <div className='profile-info'>
                  <strong>Adaptativo</strong>
                  <small>Sensível às pessoas e às experiências concretas. Aprender fazendo.</small>
                </div>
              </div>
              <div className='kolb-profile-card'>
                <div className='profile-badge-wrapper'>
                  <Badge pill color='info'>DO</Badge>
                </div>
                <div className='profile-info'>
                  <strong>Divergente</strong>
                  <small>Criativo e observador. Bom em brainstorm e visão geral.</small>
                </div>
              </div>
              <div className='kolb-profile-card'>
                <div className='profile-badge-wrapper'>
                  <Badge pill color='warning'>RO</Badge>
                </div>
                <div className='profile-info'>
                  <strong>Assimilador</strong>
                  <small>Teórico e lógico. Prefere conceitos e modelos abstratos.</small>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Distribution Result */}
      {distributionResult && (
        <Card className='card-section mb-4 card-result'>
          <div className='section-header'>
            <div className='step-badge success'>✓</div>
            <h5>Distribuição Concluída</h5>
          </div>

          <Alert color='success'>
            <i className='ri-check-line me-2'></i>
            <strong>{distributionResult.totalStudentsDistributed}</strong>{' '}
            estudantes distribuídos em{' '}
            <strong>{distributionResult.totalGroupsProcessed}</strong> grupo
            {distributionResult.totalGroupsProcessed !== 1 ? 's' : ''}
          </Alert>

          <div className='distribution-details'>
            {distributionResult.distributionsByGroup.map((dist, idx) => (
              <div key={idx} className='distribution-item'>
                <div className='dist-header'>
                  <span className='dist-group-name'>{dist.groupName}</span>
                  <Badge color={getStrategyColor(dist.strategy)}>
                    {dist.strategy}
                  </Badge>
                </div>
                <div className='dist-stats'>
                  <span className='stat-badge'>
                    {dist.studentsAdded} estudantes
                  </span>
                  <div className='dist-profiles'>
                    {dist.kolbProfiles.map((profile) => (
                      <Badge key={profile} color={getKolbColor(profile)}>
                        {profile}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {distributionResult.errors.length > 0 && (
            <Alert color='danger' className='mt-3'>
              <strong>Erros durante a distribuição:</strong>
              <ul className='mb-0 mt-2'>
                {distributionResult.errors.map((err, idx) => (
                  <li key={idx}>
                    {err.message}
                    {err.details && <small> ({err.details})</small>}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <Button
            color='primary'
            className='mt-3'
            onClick={() => {
              clearSelection();
              setIsModalOpen(false);
            }}
          >
            Nova Distribuição
          </Button>
        </Card>
      )}

      {/* Action Buttons */}
      {selectedCourse && groups.length > 0 && !distributionResult && (
        <Row className='mb-4'>
          <Col>
            <Button
              color='primary'
              size='lg'
              onClick={() => setIsModalOpen(true)}
              disabled={loadingDistribution === STATUSPAGE.LOADING}
              className='w-100'
            >
              {loadingDistribution === STATUSPAGE.LOADING ? (
                <>
                  <Spinner size='sm' className='me-2' />
                  Distribuindo...
                </>
              ) : (
                <>
                  <i className='ri-shuffle-line me-2'></i>
                  Distribuir Estudantes
                </>
              )}
            </Button>
          </Col>
        </Row>
      )}

      {/* Confirmation Modal */}
      <KolbConfirmationModal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        onConfirm={handleDistribute}
        isDistributingAll={isDistributingAll}
        course={
          selectedCourse
            ? {
                name: selectedCourse.name,
                semester: selectedCourse.semester,
                organizationName: 'Organização',
              }
            : undefined
        }
        groupsCount={
          selectedGroupIds.length === 0 ? groups.length : selectedGroupIds.length
        }
        studentsCount={getTotalStudentsWithKolb()}
        loading={loadingDistribution}
      />
        </div>
      </CardBody>
    </Card>
  );
};

function getKolbColor(profile: string): string {
  const colors: Record<string, string> = {
    AC: 'primary',
    AE: 'success',
    DO: 'info',
    RO: 'warning',
    undefined: 'secondary',
  };
  return colors[profile] || 'secondary';
}

function getKolbLabel(profile: string): string {
  const labels: Record<string, string> = {
    AC: 'AC',
    AE: 'AE',
    DO: 'DO',
    RO: 'RO',
    undefined: 'Desconhecido',
  };
  return labels[profile] || profile;
}

function getStrategyColor(strategy: string): string {
  const colors: Record<string, string> = {
    ideal: 'success',
    balanced: 'info',
    random: 'warning',
  };
  return colors[strategy] || 'secondary';
}
